import type {
  AnalyticsQuery,
  AnalyticsQueryFilters,
} from "schemas/generated/analyticsRead";

import { minutesToMilliseconds } from "date-fns";
import { currencySchema } from "schemas/project";
import { z } from "zod";

export const DEFAULT_MAIN_RANGE_ID = "f13ffb7f-ea60-474c-a3dd-669070b5f8a2";
export const DEFAULT_COMPARE_AT_RANGE_ID =
  "a24a44db-3d1b-4ad7-a7c5-de1da66cad95";

export const metricNameSchema = z
  .enum([
    "conversions",
    "views",
    "unique_sessions",
    "conversion_rates",
    "revenue",
    "revenue_per_session",
    "average_order_value",
  ])
  .describe("MetricName");

/**
 * --------------------------------
 * Schemas for AnalyticsResponse
 * --------------------------------
 */

const rangeResultMetadataSchema = z.object({
  elementId: z.uuid().nullable(),
  title: z.string(),
  url: z.string(),
});

const rangeResultMetricsSchema = z.record(
  metricNameSchema,
  z.array(z.number()),
);

export const rangeResultSchema = z
  .object({
    urlPath: z.string(),
    metadata: rangeResultMetadataSchema,
    rangeId: z.uuid(),
    metrics: rangeResultMetricsSchema,
  })
  .describe("RangeResult");

const totalRowsCountSchema = z.number().min(0);

export const analyticsResponseSchema = z
  .object({
    totalRowsCount: totalRowsCountSchema,
    rangeResults: z.array(rangeResultSchema),
  })
  .describe("AnalyticsResponse");

/**
 * --------------------------------
 * Schemas for AnalyticsQuery
 * --------------------------------
 */

const urlHostsSchema = z.array(z.string());
const offsetSchema = z.number().min(0);
const limitSchema = z.number().positive().max(100);

export const orderSchema = z.enum(["ASC", "DESC"]).describe("Order");

// biome-ignore lint/style/noEnum: This is a legacy enum, we should convert to a string union
export enum PageTypeEnum {
  ALL_PAGES = "allPages",
  ENTRY_PAGES = "entryPages",
}

export const pageTypeEnumSchema = z
  .enum(PageTypeEnum)
  .meta({ description: "PageTypeEnum", origin: "nativeEnum" });

// biome-ignore lint/style/noEnum: This is a legacy enum, we should convert to a string union
export enum ConditionOperatorEnum {
  EQUALS = "equals",
  DOES_NOT_EQUAL = "does_not_equal",
  CONTAINS = "contains",
  DOES_NOT_CONTAIN = "does_not_contain",
}

export const conditionOperatorEnumSchema = z
  .enum(ConditionOperatorEnum)
  .meta({ description: "ConditionOperatorEnum", origin: "nativeEnum" });

export const filterConditionSchema = z
  .discriminatedUnion("operator", [
    z.object({
      operator: z
        .literal(ConditionOperatorEnum.EQUALS)
        .describe("override:ConditionOperatorEnum.EQUALS"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z
        .literal(ConditionOperatorEnum.DOES_NOT_EQUAL)
        .describe("override:ConditionOperatorEnum.DOES_NOT_EQUAL"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z
        .literal(ConditionOperatorEnum.CONTAINS)
        .describe("override:ConditionOperatorEnum.CONTAINS"),
      value: z.string(),
    }),
    z.object({
      operator: z
        .literal(ConditionOperatorEnum.DOES_NOT_CONTAIN)
        .describe("override:ConditionOperatorEnum.DOES_NOT_CONTAIN"),
      value: z.string(),
    }),
  ])
  .describe("FilterCondition");

export const filterConditionsSchema = z
  .array(filterConditionSchema)
  .describe("FilterConditions");

export const urlParamsSchema = z
  .record(z.string(), filterConditionsSchema)
  .describe("QueryFiltersUrlParams");

export const filterSchema = z
  .object({
    urlPath: filterConditionsSchema,
    pageType: z.enum(PageTypeEnum).describe("PageTypeEnum"),
    urlParams: urlParamsSchema,
  })
  .describe("AnalyticsQueryFilters");

export const queryRangeSchema = z
  .object({
    id: z.uuid(),
    startDatetime: z.number().min(0),
    endDatetime: z.number().positive(),
    interval: z.number().min(10),
  })
  .describe("QueryRange");

export const adjustedRangeSchema = queryRangeSchema
  .superRefine((input, ctx) => {
    const startDatetime = input.startDatetime;
    const endDatetime = input.endDatetime;
    const interval = input.interval;

    if (endDatetime <= startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "range.endDatetime must be after range.startDatetime",
        path: ["sort", "endDatetime"],
      });
    }

    if (interval > endDatetime - startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval time must be shorter than the time between range.startDatetime and range.endDatetime",
        path: ["range", "interval"],
      });
    }

    if ((endDatetime - startDatetime) % interval != 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval doesn't fit into the range: range.endDatetime must be aligned with (i.e. a multiple of) the interval from range.startDatetime",
        path: ["range", "interval"],
      });
    }

    if (interval % minutesToMilliseconds(10) != 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Interval must be a multiple of 10 minutes",
        path: ["range", "interval"],
      });
    }
  })
  .describe("AdjustedRange");

export const queryRangesSchema = z
  .object({
    mainRange: queryRangeSchema,
    compareAtRanges: z.array(queryRangeSchema),
  })
  .describe("QueryRanges");

export const analyticsQuerySchema = z
  .object({
    /**
     * Workspace is always required. This is how we
     * check the individual paths to be sure we're looking up the correct ones for the workspace.
     */
    urlHosts: urlHostsSchema,
    /**
     * The metrics that are directly stored in ClickHouse's timeseries_records table, and/or metrics
     * that need to be calculated from metrics that are stored in ClickHouse. E.g. "conversions",
     * "views", "conversion_rates".
     */
    metrics: z.array(metricNameSchema).min(1),
    /**
     * Sorting order for the sorting metric.
     */
    order: orderSchema,
    /**
     * Sort based on a given metric name.
     */
    sortMetric: metricNameSchema,
    /**
     * The range being considered, including the interval (in MINUTES) of the buckets that we want to retrieve. E.g. if interval = 30, then we're
     * aggregating the metric calculation in buckets of 30min.
     */
    ranges: queryRangesSchema,
    /**
     * Offset used in pagination.
     */
    offset: offsetSchema,
    /**
     * Limit of the results per page.
     */
    limit: limitSchema,
    filters: filterSchema,
    targetCurrency: currencySchema.nullish(),
    mode: z
      .enum(["groupByUrlPath", "groupByDomain", "funnel"])
      .prefault("groupByUrlPath"),
    isFractional: z.boolean().prefault(false),
  })
  .superRefine((input, ctx) => {
    if (!input.metrics.includes(input.sortMetric)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sort value must be one of of the specified metrics.",
        path: ["sort"],
      });
    }
  })
  .describe("AnalyticsQuery");

export type AnalyticsQueryWithoutRanges = Omit<AnalyticsQuery, "ranges">;

/**
 * NOTE (Max, 2024-09-23): Filters that are used in sql.ts to filter out individual
 * events, regardless of the pageType (entryPages | allPages). We're not including
 * pageType as this one is used to alter the construction of CTEs, whereas the
 * SQL WHERE clause for the other filters remains the same, regardless of pageType.
 */
export type QueryFiltersRoot = Pick<AnalyticsQueryFilters, "urlPath">;

/**
 * --------------------------------
 * Schemas for retrieving urlParams
 * --------------------------------
 */

export const findUrlParamsQuerySchema = z
  .object({
    urlHosts: urlHostsSchema,
    order: orderSchema,
    offset: offsetSchema,
    limit: limitSchema,
    key: z.string(),
    value: z.string(),
  })
  .describe("FindUrlParamsQuery");

export const findUrlParamsResponseSchema = z
  .object({
    urlParams: z.array(z.string()),
  })
  .describe("FindUrlParamsResponse");

export const findSubDomainsForUrlHostsResponseSchema = z
  .array(
    z.object({
      urlHost: z.string(),
      isRootDomain: z.boolean(),
    }),
  )
  .describe("FindSubDomainsForUrlHostsResponse");

export const findAggregatedQuerySchema = z
  .object({
    analyticsQuery: analyticsQuerySchema,
    minSessionsThreshold: z.number().min(1),
  })
  .superRefine((input, ctx) => {
    const { startDatetime, endDatetime, interval } =
      input.analyticsQuery.ranges.mainRange;

    // NOTE (Max, 2025-02-26): The concept of interval doens't make sense when
    // getting the average/median. We should always be looking at the entire range.
    // If the interval is not equal to the entire range, then our AnalyticsApiService won't get the
    // aggregated data over the entire rage: it'll only return the results for the 1st interval, which
    // is not what's intended
    if (interval !== endDatetime - startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval must be equal to the difference between startDatetime and endDatetime.",
        path: ["interval"],
      });
    }
  })
  .describe("FindAggregatedQuery");

export const aggregatedResultSchema = z
  .object({
    metric: z.enum(["conversion_rates"]),
    group: z.enum([
      "all",
      "homepage",
      "/pages",
      "/products",
      "/collections",
      "/blogs",
    ]),
    average: z.number(),
    median: z.number(),
  })
  .describe("AggregatedResult");

export const findAggregatedResponseSchema = z
  .object({
    aggregatedResults: z.array(aggregatedResultSchema),
  })
  .describe("FindAggregatedResponse");
